import React from "react";
import { motion, Variants } from "framer-motion";
import logo from "../../../src/assets/logos/360WhiteLogo.svg";
import { IAuthLayout } from "Interfaces/interface.auth";

const AuthLayout: React.FC<IAuthLayout> = ({
  title,
  backgroundImage,
  benefits = [],
  children,
}) => {
  const containerVariants: Variants = {
    hidden: { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 60,
        damping: 18,
        when: "beforeChildren",
        staggerChildren: 0.5,
      },
    },
  };

  const childVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 20, damping: 5 },
    },
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-white dark:bg-neutral-900">
      {/* Left Side */}
      <motion.div
        className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 bg-gradient-to-br from-[#0068ff] via-[#0068ff] to-[#ffa267] text-white min-h-[30vh] md:min-h-screen md:max-h-none relative overflow-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <img
          src={backgroundImage}
          alt="Auth Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-transparent backdrop-blur-sm z-0" />
        <motion.div
          className="relative z-10 px-4 w-full text-center flex flex-col items-center gap-2"
          variants={childVariants}
        >
          <img
            src={logo}
            alt="360 Customizer Logo"
            className="h-12 md:h-20 lg:h-12 xl:h-20 2xl:h-24"
            loading="lazy"
            decoding="async"
          />

          <h1 className="text-3xl font-bold mb-4">{title}</h1>

          {/* Show benefits only on desktop */}
          <div className="hidden lg:grid w-full grid-cols-1 sm:grid-cols-2 gap-3">
            {benefits.map((b, idx) => (
              <motion.div
                key={idx}
                className="bg-white/10 border border-white/20 rounded-xl p-4 text-center backdrop-blur-md hover:scale-105 transition-transform 
                 h-32 w-full flex flex-col justify-between items-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <div className="p-2 bg-white/20 rounded-full mb-2">
                  <i className={`${b.icon} text-white text-xl`} />
                </div>
                <p className="text-white text-sm line-clamp-3">{b.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* Right Side */}
      <div className="md:w-full lg:w-1/2 flex flex-col justify-center p-6 md:p-10">
        <div className="w-full max-w-md mx-auto bg-white/90 dark:bg-neutral-900/90 rounded-xl">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
